import { shiftModalRoutingService } from 'modals/ShiftModal/ShiftModal.ui.context';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import { useFormBehaviors } from 'hooks';
import {
  UseFormBehaviors,
  UseFormBehaviors_Options,
} from 'hooks/useFormBehaviors/useFormBehaviors.types';

import { createContext, FC, useCallback, useContext, useMemo } from 'react';
import { API_POST_cancelPublishedShiftsById } from 'requests/POST_cancelPublishedShiftsById';
import { toastService } from 'services';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { withMemo } from 'utils/withMemo';
import { DeleteViewFormSchema } from './DeleteView.types';
import { isTimeWithinTwelveHours } from './DeleteView.utils';
import { VALIDATION_cancellationReason } from './validations/VALIDATION_cancellationReason';
import { VALIDATION_specifiedOtherReason } from './validations/VALIDATION_specifiedOtherReason';
import { useTranslation } from 'hooks/useTranslation';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { useShiftModalPropsContext } from 'modals/ShiftModal/ShiftModal.props.context';

export type DeleteViewContextType = {
  showRecurrenceRadioButtons: boolean;
  showDeleteShiftReasonTextArea: boolean;
  showLateWarning: boolean;
};

const deleteViewInitialFormState: DeleteViewFormSchema = {
  cancellationReason: [] as DeleteViewFormSchema['cancellationReason'],
  specifiedOtherReason: '',
  cancelRecurring: 'false',
};

export const DeleteViewContext = createContext(
  {} as UseFormBehaviors<DeleteViewFormSchema> & DeleteViewContextType,
);

export const DeleteViewProvider: FC = withMemo(({ children }) => {
  const { apiRequest } = useAPIActions();
  const { data } = useShiftModalDataContext();
  const order = data?.order;
  const shiftDisplayId = data?.order!.id;
  const { refreshBackgroundPage } = useShiftModalPropsContext();

  const { t } = useTranslation();

  const handleSubmit = useCallback(
    async (values) => {
      await apiRequest(
        API_POST_cancelPublishedShiftsById({ ...values, shiftId: shiftDisplayId! }),
        {
          onSuccess: () => {
            toastService.success(t('success:SHIFT_DELETED'));
            refreshBackgroundPage();
            shiftModalRoutingService.close();
          },
          onError: handleGenericErrorMessage,
        },
      );
    },
    [apiRequest, refreshBackgroundPage, shiftDisplayId, t],
  );

  const formBehaviorsOptions = useMemo(
    () =>
      ({
        isDirtyCheckEnabled: true,
        initialState: deleteViewInitialFormState,
        onSubmit: handleSubmit,
        validations: {
          specifiedOtherReason: VALIDATION_cancellationReason,
          cancellationReason: VALIDATION_specifiedOtherReason,
        },
      } as UseFormBehaviors_Options<DeleteViewFormSchema>),
    [handleSubmit],
  );

  const formUtils = useFormBehaviors<DeleteViewFormSchema>(formBehaviorsOptions);

  // Context Values
  const onChange = useCallback((event) => formUtils.onChange(event), [formUtils]);
  const { cancellationReason, specifiedOtherReason } = formUtils.values;
  const showDeleteShiftReasonTextArea = cancellationReason[0] === 'other';

  const isReasonMissing =
    !cancellationReason[0] ||
    (cancellationReason[0] === 'other' && specifiedOtherReason.length === 0);

  const { startAt, mainJane } = order!;

  const timeZone = order.timeZone?.toString();
  const isShiftAcceptedByAgencyJane = !!mainJane?.agencyCommunity;
  const showLateWarning = isTimeWithinTwelveHours(startAt, timeZone) && isShiftAcceptedByAgencyJane;

  const value: UseFormBehaviors<DeleteViewFormSchema> & DeleteViewContextType = useMemo(() => {
    return {
      ...formUtils,
      isDisabled: isReasonMissing,
      onChange,
      showRecurrenceRadioButtons: !!order!.recurrence,
      showDeleteShiftReasonTextArea,
      showLateWarning,
    };
  }, [
    data?.order,
    formUtils,
    isReasonMissing,
    onChange,
    showDeleteShiftReasonTextArea,
    showLateWarning,
  ]);

  return <DeleteViewContext.Provider value={value}>{children}</DeleteViewContext.Provider>;
});

export const useDeleteViewContext = () => useContext(DeleteViewContext);
